import {
  ficoShort,
  ficoSymbolAlt,
  registeredMark,
  scoreBoostResults
} from '@ecs/tng-common/documents/disclosures/regulatory';
import parse from 'html-react-parser';
import { IFeatureCard } from '@ecs/tng-common/interfaces';
import React from 'react';
import { DisclosureSymbol } from '@ecs/tng-common/components/disclosures/Disclosures';

export type IActionDisclosures = 'fico' | 'boost' | 'sm' | 'subscriptions';
export interface IFeatureActionCard extends IFeatureCard {
  disclosure?: IActionDisclosures;
}
export interface IActionCardsContent {
  featured: IFeatureActionCard;
  regular: IFeatureActionCard[];
}

type ActionFields =
  | 'FreeCreditReport'
  | 'FreeCreditMonitoring'
  | 'ExperianBoost'
  | 'ExperianGo'
  | 'IdentityTheftProtection'
  | 'FreeDarkWebScan'
  | 'PersonalPrivacyScan'
  | 'ExperianSmartMoney'
  | 'ExperianBillFixer'
  | 'BestPersonalLoans'
  | 'BestDebtConsolidationLoans'
  | 'BestBalanceTransferCards'
  | 'BestCashBackCards'
  | 'BestRewardsCards'
  | 'BestBadCreditCards'
  | 'BestFairCreditCards'
  | 'BestAPRCards'
  | 'BestSecuredCards'
  | 'BestTravelCards'
  | 'BestIntroBonusCards'
  | 'BestNoAnnualFeeCards'
  | 'BestLowInterestCards'
  | 'BestHighYieldSaving'
  | 'BestCDRates'
  | 'BestMoneyMarketAccounts'
  | 'BestCheckingAccounts'
  | 'AutoInsuranceComparison'
  | 'SecurityFreeze'
  | 'FraudAlert'
  | 'Disputes'
  | 'FreeExperianApp'
  | 'APRCalculator'
  | 'PersonalLoanCalculator'
  | 'CreditCardCalculator'
  | 'MortageCalculator'
  | 'CarPaymentCalculator'
  | 'SavingsCalculator';
export interface IActionCards {
  [key: string]: IActionCardsContent;
}
export const actions: Record<ActionFields, IFeatureActionCard> = {
  FreeCreditReport: {
    heading: 'Free credit report',
    subcopy: (
      <>
        Get access to your free Experian credit report and FICO{parse(registeredMark)} Score
        <DisclosureSymbol
          disclosure={ficoShort}
          symbol={ficoSymbolAlt}
        />
        .
      </>
    ),
    link: '/credit/credit-score/',
    icon: 'ecs-font-icon-frs-report-experian-score',
    spotIcon: 'icon-score.svg',
    disclosure: 'fico',
    ctaText: `Get your FICO${registeredMark} Score`,
    dataLink: 'ae-sa-cr-frcr-'
  },
  FreeCreditMonitoring: {
    heading: 'Free credit monitoring',
    subcopy: 'Stay on top of your credit and monitor your spending.',
    link: '/credit/credit-monitoring/',
    icon: 'ecs-font-icon-frs-monitoring',
    spotIcon: 'icon-monitor.svg',
    ctaText: 'Start monitoring',
    dataLink: 'ae-sa-cr-frcm-'
  },
  ExperianBoost: {
    heading: `Experian Boost${registeredMark}`,
    subcopy: (
      <>
        Instantly raise your credit scores for free—just by paying your bills.
        <DisclosureSymbol disclosure={scoreBoostResults} />
      </>
    ),
    link: '/credit/score-boost/',
    icon: 'ecs-font-icon-frs-rocket-boost',
    spotIcon: 'icon-boost.svg',
    ctaText: 'Start your boost',
    disclosure: 'boost',
    dataLink: 'ae-sa-cr-boost-'
  },
  ExperianGo: {
    heading: 'Experian Go™',
    subcopy: 'Build your credit for free and start reaching your financial goals.',
    link: '/credit/experian-go/',
    icon: 'ecs-font-icon-frs-score',
    spotIcon: 'icon-new-credit.svg',
    ctaText: 'Get started',
    dataLink: 'ae-sa-cr-go-'
  },
  IdentityTheftProtection: {
    heading: 'Identity theft protection',
    subcopy: 'Identity theft protection and fraud detection for you and your family.',
    link: '/protection/identity-theft-and-credit-protection/',
    icon: 'ecs-font-icon-idt-shield-lock',
    spotIcon: 'icon-protection-shield.svg',
    ctaText: 'Compare plans',
    dataLink: 'ae-sa-itp-prem-'
  },
  FreeDarkWebScan: {
    heading: 'Free dark web scan',
    subcopy: 'Scan for your Social Security number, email and phone number.',
    link: '/protection/free-dark-web-email-scan/',
    icon: 'ecs-font-icon-idt-dark-web',
    spotIcon: 'icon-dark-web-data.svg',
    ctaText: 'Start your free scan',
    dataLink: 'ae-sa-itp-darkweb-'
  },
  PersonalPrivacyScan: {
    heading: 'Free personal privacy scan',
    subcopy: 'Check your info and reduce your risk of hacking, spam and robocalls.',
    link: '/protection/personal-privacy-scan/',
    icon: 'ecs-font-icon-identity-protection',
    spotIcon: 'icon-id-scan.svg',
    ctaText: 'Start your free scan',
    dataLink: 'ae-sa-itp-pps-'
  },
  ExperianSmartMoney: {
    heading: 'Experian Smart Money™',
    subcopy: 'Build credit by paying your bills with your digital checking account.',
    link: '/money/experian-smart-money/',
    icon: 'ecs-font-icon-con-phone',
    spotIcon: 'icon-experian-smart-money.svg',
    ctaText: 'Sign up in minutes',
    disclosure: 'sm',
    dataLink: 'ae-sa-mny-esm-'
  },
  ExperianBillFixer: {
    heading: 'Experian BillFixer™',
    subcopy: 'Stop paying for unwanted subscriptions and lower your bills.**',
    link: '/money/manage-bills-subscriptions/',
    icon: 'ecs-font-icon-frs-bill-utility',
    spotIcon: 'icon-piggy-bank.svg',
    ctaText: 'Start saving',
    disclosure: 'subscriptions',
    dataLink: 'ae-sa-mny-billfixer-'
  },
  BestPersonalLoans: {
    heading: 'Best personal loans',
    subcopy: 'Fund home improvement projects, cover expenses and more.',
    link: '/loans/personal/',
    icon: 'ecs-font-icon-ccm-loan',
    spotIcon: 'icon-wallet.svg',
    ctaText: 'Compare loans',
    dataLink: 'ae-sa-lo-personal-'
  },
  BestDebtConsolidationLoans: {
    heading: 'Best debt consolidation loans',
    subcopy: 'Consolidate your debt and manage your payments easier.',
    link: '/loans/debt-consolidation/',
    icon: 'ecs-font-icon-frs-bank',
    spotIcon: 'icon-debt.svg',
    ctaText: 'Compare loans',
    dataLink: 'ae-sa-lo-debt-'
  },
  BestBalanceTransferCards: {
    heading: 'Best balance transfer credit cards',
    subcopy: 'Save money by transferring high-interest debt.',
    link: '/credit-cards/best-balance-transfer/',
    icon: 'ecs-font-icon-ccm-balance-transfer',
    spotIcon: 'icon-balance-transfer.svg',
    ctaText: 'Compare cards',
    dataLink: 'ae-sa-cc-transfer-'
  },
  BestCashBackCards: {
    heading: 'Best cash back credit cards',
    subcopy: 'Turn your everyday spending into cash back.',
    link: '/credit-cards/best-cash-back/',
    icon: 'ecs-font-icon-ccm-cashback',
    spotIcon: 'icon-cashback.svg',
    ctaText: 'Compare cards',
    dataLink: 'ae-sa-cc-cash-'
  },
  BestRewardsCards: {
    heading: 'Best rewards credit cards',
    subcopy: 'Make the most of your spending with a rewards card.',
    link: '/credit-cards/best-rewards/',
    icon: 'ecs-font-icon-ccm-trophy',
    spotIcon: 'icon-card-praise.svg',
    ctaText: 'Compare cards',
    dataLink: 'ae-sa-cc-rewards-'
  },
  BestBadCreditCards: {
    heading: 'Best credit cards for bad credit',
    subcopy: 'Get matched to credit cards based on your unique credit profile.',
    link: '/credit-cards/best-for-bad-credit/',
    icon: 'ecs-font-icon-frs-score-bad',
    spotIcon: 'icon-score',
    ctaText: 'Compare cards',
    dataLink: 'ae-sa-cc-bad-'
  },
  BestFairCreditCards: {
    heading: 'Best credit cards for fair credit',
    subcopy: 'Start building positive credit history and get matched to credit cards.',
    link: '/credit-cards/best-for-fair-credit/',
    icon: 'ecs-font-icon-frs-score-fair',
    spotIcon: 'icon-score',
    ctaText: 'Compare cards',
    dataLink: 'ae-sa-cc-fair-'
  },
  BestAPRCards: {
    heading: 'Best 0% intro APR credit cards',
    subcopy: 'Take advantage of no interest payments with an intro 0% APR card.',
    link: '/credit-cards/best-zero-interest/',
    icon: 'ecs-font-icon-ccm-percent-burst',
    spotIcon: 'icon-percent-burst.svg',
    ctaText: 'Compare cards',
    dataLink: 'ae-sa-cc-zero-'
  },
  BestSecuredCards: {
    heading: 'Best secured credit cards',
    subcopy: 'Control your spending and build your credit.',
    link: '/credit-cards/best-secured/',
    icon: 'ecs-font-icon-con-banking-spending',
    spotIcon: 'icon-locked-loans.svg',
    ctaText: 'Compare cards',
    dataLink: 'ae-sa-cc-secured-'
  },
  BestTravelCards: {
    heading: 'Best travel credit cards',
    subcopy: 'Earn miles and points to save money on travel with a rewards card.',
    link: '/credit-cards/best-travel/',
    icon: 'ecs-font-icon-ccm-airplane',
    spotIcon: 'icon-travel.svg',
    ctaText: 'Compare cards',
    dataLink: 'ae-sa-cc-travel-'
  },
  BestIntroBonusCards: {
    heading: 'Best intro bonus credit cards',
    subcopy: 'Earn more rewards with an intro bonus card.',
    link: '/credit-cards/best-intro-bonus/',
    icon: 'ecs-font-icon-ccm-dollar-sign',
    spotIcon: 'icon-new-item-offer.svg',
    ctaText: 'Compare cards',
    dataLink: 'ae-sa-cc-intro-'
  },
  BestNoAnnualFeeCards: {
    heading: 'Best no annual fee credit cards',
    subcopy: 'Maximize your rewards without breaking your budget.',
    link: '/credit-cards/best-no-annual-fee/',
    icon: 'ecs-font-icon-frs-calendar1',
    spotIcon: 'icon-no-fees.svg',
    ctaText: 'Compare cards',
    dataLink: 'ae-sa-cc-annual-'
  },
  BestLowInterestCards: {
    heading: 'Best low interest credit cards',
    subcopy: 'Save money with a low interest card. ',
    link: '/credit-cards/best-low-interest/',
    icon: 'ecs-font-icon-ccm-percent-burst',
    spotIcon: 'icon-percent-burst.svg',
    ctaText: 'Compare cards',
    dataLink: 'ae-sa-cc-low-'
  },
  BestHighYieldSaving: {
    heading: 'Best high-yield savings',
    subcopy: 'Find the best savings rates for your money—compare top offers now. ',
    link: '/blogs/ask-experian/best-high-yield-savings-accounts/',
    icon: 'ecs-font-icon-ccm-dollar-sign',
    spotIcon: 'icon-piggy-bank.svg',
    ctaText: 'See rates',
    dataLink: 'ae-sa-ba-saving-'
  },
  BestCDRates: {
    heading: 'Best CD rates',
    subcopy: 'Find the best CD rates for your money—compare top offers now.',
    link: '/blogs/ask-experian/best-cd-rates/',
    icon: 'ecs-font-icon-ccm-percent-box',
    spotIcon: 'icon-wallet.svg',
    ctaText: 'See rates',
    dataLink: 'ae-sa-ba-cd-'
  },
  BestMoneyMarketAccounts: {
    heading: 'Best money market accounts',
    subcopy: 'Find the best money market accounts—compare top offers now.',
    link: '/blogs/ask-experian/best-money-market-accounts/',
    icon: 'ecs-font-icon-ccm-money',
    spotIcon: 'icon-wallet.svg',
    ctaText: 'See rates',
    dataLink: 'ae-sa-ba-mma-'
  },
  BestCheckingAccounts: {
    heading: 'Best checking accounts',
    subcopy: 'Find the best checking accounts—compare top offers now.',
    link: '/blogs/ask-experian/best-checking-accounts/',
    icon: 'ecs-font-icon-frs-bank',
    spotIcon: 'icon-bank.svg',
    ctaText: 'See rates',
    dataLink: 'ae-sa-ba-checking-'
  },
  AutoInsuranceComparison: {
    heading: 'Auto insurance comparison',
    subcopy: 'Shop and compare quotes for you from over 40 top insurers.',
    link: '/insurance/car-insurance-quotes/',
    icon: 'ecs-font-icon-auto-front',
    spotIcon: 'icon-car.svg',
    ctaText: 'Get quotes',
    dataLink: 'ae-sa-in-auto-'
  },
  SecurityFreeze: {
    heading: 'Security freeze',
    subcopy: 'Limit access to your Experian credit report.',
    link: '/help/credit-freeze/',
    icon: 'ecs-font-icon-frs-security-freeze',
    spotIcon: 'icon-security-freeze.svg',
    ctaText: 'Create a free account',
    dataLink: 'ae-sa-reg-freeze-'
  },
  FraudAlert: {
    heading: 'Fraud alert',
    subcopy: 'Add or remove a fraud alert on your Experian credit file.',
    link: '/help/fraud-alert/',
    icon: 'ecs-font-icon-idt-fraud',
    spotIcon: 'icon-thin-file.svg',
    ctaText: 'Place a fraud alert',
    dataLink: 'ae-sa-reg-fraud-'
  },
  Disputes: {
    heading: 'Disputes',
    subcopy: 'Correct inaccuracies on your Experian credit file. ',
    link: '/disputes/main.html ',
    icon: 'ecs-font-icon-frs-dispute',
    spotIcon: 'icon-secure-message.svg',
    ctaText: 'File a dispute',
    dataLink: 'ae-sa-reg-dispute-'
  },
  FreeExperianApp: {
    heading: 'Free Experian app',
    subcopy: 'Manage your finances any time, anywhere on your phone.',
    link: '/credit/experian-app/',
    icon: 'ecs-font-icon-frs-report-experian',
    spotIcon: 'icon-experian-phone.svg',
    ctaText: 'Download the free app',
    dataLink: 'ae-sa-cr-app-'
  },
  APRCalculator: {
    heading: 'APR calculator',
    subcopy: 'Calculate a loan’s APR based on its interest rate, fees and terms.',
    link: '/blogs/ask-experian/apr-calculator/',
    icon: 'ecs-font-icon-con-calculator',
    spotIcon: 'icon-calculator.svg',
    ctaText: 'Learn more',
    dataLink: 'ae-sa-calc-apr-'
  },
  PersonalLoanCalculator: {
    heading: 'Personal loan calculator',
    subcopy: 'Estimate your monthly payment, interest rate and repayment term.',
    link: '/blogs/ask-experian/personal-loan-calculator/',
    icon: 'ecs-font-icon-con-calculator',
    spotIcon: 'icon-calculator.svg',
    ctaText: 'Learn more',
    dataLink: 'ae-sa-calc-personal-'
  },
  CreditCardCalculator: {
    heading: 'Credit card payoff calculator',
    subcopy: 'Calculate the number of payments you’ll need to make.',
    link: '/blogs/ask-experian/credit-card-payoff-calculator/',
    icon: 'ecs-font-icon-con-calculator',
    spotIcon: 'icon-calculator.svg',
    ctaText: 'Learn more',
    dataLink: 'ae-sa-calc-cc-'
  },
  MortageCalculator: {
    heading: 'Mortgage calculator',
    subcopy: 'Understand how rates affect your monthly payment and the total cost.',
    link: '/blogs/ask-experian/mortgage-calculator/',
    icon: 'ecs-font-icon-con-calculator',
    spotIcon: 'icon-calculator.svg',
    ctaText: 'Learn more',
    dataLink: 'ae-sa-calc-mortgage-'
  },
  CarPaymentCalculator: {
    heading: 'Car payment calculator',
    subcopy: 'Compare estimated payments with different term lengths and rates.',
    link: '/blogs/ask-experian/car-payment-calculator/',
    icon: 'ecs-font-icon-con-calculator',
    spotIcon: 'icon-calculator.svg',
    ctaText: 'Learn more',
    dataLink: 'ae-sa-calc-carpay-'
  },
  SavingsCalculator: {
    heading: 'Savings calculator',
    subcopy: 'Estimate how much total interest you’ll earn.',
    link: '/blogs/ask-experian/savings-calculator/',
    icon: 'ecs-font-icon-con-calculator',
    spotIcon: 'icon-calculator.svg',
    ctaText: 'Learn more',
    dataLink: 'ae-sa-calc-saving-'
  }
};
export const actionCards: IActionCards = {
  home: {
    featured: actions.FreeExperianApp,
    regular: [
      actions.SecurityFreeze,
      actions.FraudAlert,
      actions.Disputes,
      actions.FreeCreditReport,
      actions.IdentityTheftProtection,
      actions.FreeDarkWebScan,
      actions.ExperianBoost,
      actions.AutoInsuranceComparison,
      actions.ExperianBillFixer,
      actions.ExperianGo,
      actions.BestHighYieldSaving,
      actions.BestCDRates,
      actions.BestPersonalLoans,
      actions.BestRewardsCards,
      actions.APRCalculator,
      actions.SavingsCalculator
    ]
  },
  banking: {
    featured: actions.ExperianSmartMoney,
    regular: [
      actions.BestHighYieldSaving,
      actions.BestCDRates,
      actions.SavingsCalculator,
      actions.ExperianBillFixer
    ]
  },
  cd: {
    featured: actions.BestCDRates,
    regular: [
      actions.BestHighYieldSaving,
      actions.BestMoneyMarketAccounts,
      actions.SavingsCalculator,
      actions.ExperianSmartMoney
    ]
  },
  'checking-account': {
    featured: actions.ExperianSmartMoney,
    regular: [
      actions.BestHighYieldSaving,
      actions.BestCDRates,
      actions.SavingsCalculator,
      actions.ExperianSmartMoney
    ]
  },
  'money-market': {
    featured: actions.BestMoneyMarketAccounts,
    regular: [
      actions.BestHighYieldSaving,
      actions.BestCDRates,
      actions.SavingsCalculator,
      actions.ExperianSmartMoney
    ]
  },
  'saving-account': {
    featured: actions.BestHighYieldSaving,
    regular: [
      actions.BestCDRates,
      actions.BestMoneyMarketAccounts,
      actions.SavingsCalculator,
      actions.ExperianSmartMoney
    ]
  },
  'credit-cards': {
    featured: actions.BestRewardsCards,
    regular: [
      actions.BestBalanceTransferCards,
      actions.BestCashBackCards,
      actions.BestTravelCards,
      actions.CreditCardCalculator,
      actions.BestIntroBonusCards,
      actions.BestAPRCards,
      actions.BestNoAnnualFeeCards,
      actions.BestBadCreditCards,
      actions.BestFairCreditCards
    ]
  },
  apr: {
    featured: actions.BestAPRCards,
    regular: [
      actions.APRCalculator,
      actions.BestHighYieldSaving,
      actions.BestBalanceTransferCards,
      actions.BestLowInterestCards,
      actions.BestRewardsCards,
      actions.BestCashBackCards,
      actions.BestTravelCards,
      actions.BestIntroBonusCards,
      actions.BestNoAnnualFeeCards,
      actions.CreditCardCalculator
    ]
  },
  'balance-transfers': {
    featured: actions.BestBalanceTransferCards,
    regular: [
      actions.BestDebtConsolidationLoans,
      actions.BestAPRCards,
      actions.BestIntroBonusCards,
      actions.CreditCardCalculator,
      actions.BestCashBackCards,
      actions.BestRewardsCards
    ]
  },
  'cash-back-credit-cards': {
    featured: actions.BestCashBackCards,
    regular: [
      actions.BestRewardsCards,
      actions.BestBalanceTransferCards,
      actions.BestTravelCards,
      actions.CreditCardCalculator,
      actions.BestIntroBonusCards,
      actions.BestAPRCards
    ]
  },
  'credit-card-basics': {
    featured: actions.BestRewardsCards,
    regular: [
      actions.BestBalanceTransferCards,
      actions.BestCashBackCards,
      actions.BestTravelCards,
      actions.CreditCardCalculator,
      actions.BestIntroBonusCards,
      actions.BestAPRCards
    ]
  },
  'rewards-credit-cards': {
    featured: actions.BestRewardsCards,
    regular: [
      actions.BestCashBackCards,
      actions.BestBalanceTransferCards,
      actions.BestTravelCards,
      actions.BestIntroBonusCards,
      actions.BestAPRCards,
      actions.CreditCardCalculator
    ]
  },
  'secured-credit-cards': {
    featured: actions.BestSecuredCards,
    regular: [
      actions.BestBadCreditCards,
      actions.BestFairCreditCards,
      actions.FreeExperianApp,
      actions.CreditCardCalculator
    ]
  },
  'travel-credit-cards': {
    featured: actions.BestTravelCards,
    regular: [
      actions.BestBalanceTransferCards,
      actions.BestCashBackCards,
      actions.BestRewardsCards,
      actions.BestAPRCards,
      actions.BestIntroBonusCards,
      actions.CreditCardCalculator
    ]
  },
  'credit-advice': {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  'establishing-credit': {
    featured: actions.ExperianGo,
    regular: [
      actions.BestSecuredCards,
      actions.BestBadCreditCards,
      actions.ExperianBoost,
      actions.FreeExperianApp
    ]
  },
  'improve-credit': {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.ExperianGo
    ]
  },
  'life-stages': {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.IdentityTheftProtection,
      actions.FreeExperianApp
    ]
  },
  'death-of-a-relative': {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  divorce: {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  employment: {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  kids: {
    featured: actions.ExperianGo,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  marriage: {
    featured: actions.FreeCreditReport,
    regular: [
      actions.BestPersonalLoans,
      actions.BestAPRCards,
      actions.FreeCreditMonitoring,
      actions.ExperianBoost
    ]
  },
  students: {
    featured: actions.FreeExperianApp,
    regular: [
      actions.ExperianGo,
      actions.ExperianBoost,
      actions.BestHighYieldSaving,
      actions.AutoInsuranceComparison
    ]
  },
  'report-advice': {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  bankruptcy: {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  'charge-off': {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  'closed-accounts': {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  collections: {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  cosigning: {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  'credit-limit': {
    featured: actions.BestRewardsCards,
    regular: [
      actions.BestBalanceTransferCards,
      actions.BestCashBackCards,
      actions.BestTravelCards,
      actions.CreditCardCalculator,
      actions.BestIntroBonusCards,
      actions.BestAPRCards
    ]
  },
  'credit-repair': {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  'denied-credit': {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  dispute: {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  foreclosure: {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  inquiry: {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  'late-payment': {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  'negative-information': {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  repossession: {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  'score-advice': {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.Disputes,
      actions.FreeExperianApp
    ]
  },
  'fraud-and-identity-theft': {
    featured: actions.IdentityTheftProtection,
    regular: [
      actions.SecurityFreeze,
      actions.FraudAlert,
      actions.FreeDarkWebScan,
      actions.PersonalPrivacyScan,
      actions.FreeCreditReport,
      actions.FreeCreditMonitoring
    ]
  },
  cybersecurity: {
    featured: actions.IdentityTheftProtection,
    regular: [
      actions.SecurityFreeze,
      actions.FraudAlert,
      actions.FreeDarkWebScan,
      actions.PersonalPrivacyScan,
      actions.FreeCreditReport,
      actions.FreeCreditMonitoring
    ]
  },
  'data-breach': {
    featured: actions.IdentityTheftProtection,
    regular: [
      actions.SecurityFreeze,
      actions.FraudAlert,
      actions.FreeDarkWebScan,
      actions.PersonalPrivacyScan,
      actions.FreeCreditReport,
      actions.FreeCreditMonitoring
    ]
  },
  'fraud-alert': {
    featured: actions.IdentityTheftProtection,
    regular: [
      actions.SecurityFreeze,
      actions.FraudAlert,
      actions.FreeDarkWebScan,
      actions.PersonalPrivacyScan,
      actions.FreeCreditReport,
      actions.FreeCreditMonitoring
    ]
  },
  prevention: {
    featured: actions.IdentityTheftProtection,
    regular: [
      actions.SecurityFreeze,
      actions.FraudAlert,
      actions.FreeDarkWebScan,
      actions.PersonalPrivacyScan,
      actions.FreeCreditReport,
      actions.FreeCreditMonitoring
    ]
  },
  'security-freeze': {
    featured: actions.IdentityTheftProtection,
    regular: [
      actions.SecurityFreeze,
      actions.FraudAlert,
      actions.FreeDarkWebScan,
      actions.PersonalPrivacyScan,
      actions.FreeCreditReport,
      actions.FreeCreditMonitoring
    ]
  },
  insurance: {
    featured: actions.AutoInsuranceComparison,
    regular: [
      actions.FreeExperianApp,
      actions.FreeCreditReport,
      actions.ExperianBoost,
      actions.ExperianBillFixer
    ]
  },
  'auto-insurance': {
    featured: actions.AutoInsuranceComparison,
    regular: [
      actions.FreeExperianApp,
      actions.FreeCreditReport,
      actions.ExperianBoost,
      actions.ExperianBillFixer
    ]
  },
  'health-insurance': {
    featured: actions.FreeExperianApp,
    regular: [
      actions.AutoInsuranceComparison,
      actions.FreeCreditReport,
      actions.ExperianBoost,
      actions.ExperianBillFixer
    ]
  },
  'home-insurance': {
    featured: actions.FreeExperianApp,
    regular: [
      actions.AutoInsuranceComparison,
      actions.FreeCreditReport,
      actions.ExperianBoost,
      actions.ExperianBillFixer
    ]
  },
  'insurance-reviews': {
    featured: actions.AutoInsuranceComparison,
    regular: [
      actions.FreeExperianApp,
      actions.FreeCreditReport,
      actions.ExperianBoost,
      actions.ExperianBillFixer
    ]
  },
  'life-insurance': {
    featured: actions.FreeExperianApp,
    regular: [
      actions.AutoInsuranceComparison,
      actions.FreeCreditReport,
      actions.ExperianBoost,
      actions.ExperianBillFixer
    ]
  },
  'pet-insurance': {
    featured: actions.FreeExperianApp,
    regular: [
      actions.AutoInsuranceComparison,
      actions.FreeCreditReport,
      actions.ExperianBoost,
      actions.ExperianBillFixer
    ]
  },
  loans: {
    featured: actions.BestPersonalLoans,
    regular: [
      actions.BestBalanceTransferCards,
      actions.BestDebtConsolidationLoans,
      actions.CreditCardCalculator,
      actions.MortageCalculator
    ]
  },
  'auto-loans': {
    featured: actions.FreeExperianApp,
    regular: [
      actions.FreeCreditReport,
      actions.ExperianBoost,
      actions.CarPaymentCalculator,
      actions.BestPersonalLoans
    ]
  },
  'loan-basics': {
    featured: actions.BestPersonalLoans,
    regular: [
      actions.BestBalanceTransferCards,
      actions.BestDebtConsolidationLoans,
      actions.CreditCardCalculator,
      actions.MortageCalculator
    ]
  },
  'personal-loans': {
    featured: actions.BestPersonalLoans,
    regular: [
      actions.BestBalanceTransferCards,
      actions.BestDebtConsolidationLoans,
      actions.BestAPRCards,
      actions.PersonalLoanCalculator
    ]
  },

  'student-loans': {
    featured: actions.FreeExperianApp,
    regular: [
      actions.ExperianGo,
      actions.ExperianBoost,
      actions.BestHighYieldSaving,
      actions.AutoInsuranceComparison
    ]
  },
  'news-trends': {
    featured: actions.FreeExperianApp,
    regular: [
      actions.ExperianBoost,
      actions.ExperianSmartMoney,
      actions.ExperianBillFixer,
      actions.BestHighYieldSaving,
      actions.BestCDRates,
      actions.BestMoneyMarketAccounts,
      actions.BestPersonalLoans,
      actions.BestRewardsCards
    ]
  },
  research: {
    featured: actions.FreeExperianApp,
    regular: [
      actions.ExperianBoost,
      actions.ExperianSmartMoney,
      actions.ExperianBillFixer,
      actions.BestHighYieldSaving,
      actions.BestCDRates,
      actions.BestMoneyMarketAccounts,
      actions.BestPersonalLoans,
      actions.BestRewardsCards
    ]
  },
  'personal-finance': {
    featured: actions.FreeExperianApp,
    regular: [
      actions.ExperianBoost,
      actions.ExperianSmartMoney,
      actions.ExperianBillFixer,
      actions.BestHighYieldSaving,
      actions.BestCDRates,
      actions.BestMoneyMarketAccounts,
      actions.BestPersonalLoans,
      actions.BestRewardsCards,
      actions.SavingsCalculator,
      actions.APRCalculator
    ]
  },
  budgeting: {
    featured: actions.ExperianBillFixer,
    regular: [
      actions.ExperianSmartMoney,
      actions.BestHighYieldSaving,
      actions.BestCDRates,
      actions.SavingsCalculator
    ]
  },
  debt: {
    featured: actions.BestDebtConsolidationLoans,
    regular: [
      actions.BestBalanceTransferCards,
      actions.BestPersonalLoans,
      actions.CreditCardCalculator,
      actions.FreeCreditMonitoring
    ]
  },
  heloc: {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.MortageCalculator,
      actions.FreeExperianApp
    ]
  },
  investing: {
    featured: actions.BestMoneyMarketAccounts,
    regular: [
      actions.BestHighYieldSaving,
      actions.BestCDRates,
      actions.ExperianSmartMoney,
      actions.APRCalculator
    ]
  },
  mortgage: {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.MortageCalculator,
      actions.FreeExperianApp
    ]
  },
  rentals: {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.MortageCalculator,
      actions.FreeExperianApp
    ]
  },
  refinance: {
    featured: actions.FreeCreditReport,
    regular: [
      actions.FreeCreditMonitoring,
      actions.ExperianBoost,
      actions.MortageCalculator,
      actions.FreeExperianApp
    ]
  },
  retirement: {
    featured: actions.BestHighYieldSaving,
    regular: [
      actions.ExperianSmartMoney,
      actions.BestCDRates,
      actions.ExperianBillFixer,
      actions.SavingsCalculator
    ]
  },
  saving: {
    featured: actions.BestHighYieldSaving,
    regular: [
      actions.ExperianSmartMoney,
      actions.BestCDRates,
      actions.BestMoneyMarketAccounts,
      actions.SavingsCalculator
    ]
  },
  taxes: {
    featured: actions.FreeExperianApp,
    regular: [
      actions.IdentityTheftProtection,
      actions.ExperianSmartMoney,
      actions.BestHighYieldSaving,
      actions.BestCDRates
    ]
  },
  'small-business': {
    featured: actions.FreeExperianApp,
    regular: [
      actions.BestPersonalLoans,
      actions.BestAPRCards,
      actions.FreeCreditMonitoring,
      actions.ExperianBoost
    ]
  }
};
